import React, { useEffect, useMemo, useState } from 'react'
import Select from 'react-select'
import styled from 'styled-components'

import Spinner from '../Spinner'
import IconSuccess from '../IconSuccess'
import IconError from '../IconError'
import { ChargePoint, FirmwareType, useFirmwareGetFirmwaresByFilterQuery } from '../../common/redux/api/web-api'
import { Button } from '../../common/components/Button'
import useText from '../../common/hooks/useText'
import { compareSemanticVersions, getFirmwareVersion } from '../../common/services/utilsService'
import BootstrapModalLayout from '../BootstrapModalLayout'
import useKeyboardHandle from '../../common/hooks/useKeyboardHandle'

const SErrorAlert = styled.div`
    font-family: ${({ theme }) => theme.font.name};
    font-size: 16px;
    padding: 15px;
    font-weight: 300;
    border: 1px solid transparent;
    border-radius: 4px;
    color: #a94442;
    background-color: #f2dede;
    border-color: #ebccd1;
`

const SFilterLabel = styled.label`
    font-family: ${({ theme }) => theme.font.name};
    font-size: 16px;
    font-weight: 300;
    line-height: 24px;
    text-align: left;
`

const SFormContainer = styled.div`
    border-radius: 8px;
    padding: 36px 130px;
    margin-top: 70px;

    @media (max-width: 900px) {
        padding: 20px 15px;
        margin-top: 40px;
    }
`

const SUpdateFormContainer = styled.div`
    padding-bottom: 40px;

    @media (max-width: 900px) {
        padding-bottom: 0;
    }
`

const SSearchResultsNumber = styled.p`
    font-family: ${({ theme }) => theme.font.name};
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    text-align: left;
`

const SCentered = styled.div`
    text-align: center;
`

const SSpinnerTitle = styled.div`
    font-family: ${({ theme }) => theme.font.name};
    font-size: 16px;
    font-weight: 300;
    line-height: 24px;
`

const SButtonContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-around;
`

const SFinishModalContainer = styled.div`
    padding-bottom: 48px;
    display: flex;
    flex-direction: column;
    color: #000;
    font-size: 16px;
    font-weight: 400;
`

const SIconContainer = styled.div`
    padding-bottom: 28px;
`

type UpdateChargersFirmwareModalProps = {
    productId: number
    firmwareType: FirmwareType
    chargePoints: ChargePoint[]
    productTitle: string
    firmwareTitle: string
    onSubmit: (version: number) => Promise<void>
    onCancel: (refresh?: boolean) => void
}

const UpdateChargersFirmwareModal = ({
    productId,
    firmwareType,
    productTitle,
    firmwareTitle,
    chargePoints,
    onSubmit,
    onCancel,
}: UpdateChargersFirmwareModalProps): React.ReactElement => {
    const text = useText()
    const isOcpp = chargePoints[0].isOcpp
    const [selectedVersion, setSelectedVersion] = useState<number | null>(null)
    const [isSubmitted, setIsSubmitted] = useState(false)
    const [isFinished, setIsFinished] = useState(false)
    const [isDifferentFirmwareTypes, setIsDifferentFirmwareTypes] = useState(false)
    const [error, setError] = useState(null)
    const { data: firmwaresResponse } = useFirmwareGetFirmwaresByFilterQuery({
        productId,
        ProductId: productId,
        firmwareType,
        ocppEnabled: isOcpp,
    })

    const firmwaresOptions = useMemo(() => {
        if (firmwaresResponse) {
            const maxInstalled = chargePoints.reduce((previousValue, currentValue) => {
                return compareSemanticVersions(
                    isOcpp ? getFirmwareVersion(previousValue.ocppConfigKeys) : previousValue.installedFirmwareVersion,
                    isOcpp ? getFirmwareVersion(currentValue.ocppConfigKeys) : currentValue.installedFirmwareVersion
                )
                    ? previousValue
                    : currentValue
            })

            const firmwares =
                firmwaresResponse?.items &&
                firmwaresResponse?.items.filter((fw) =>
                    compareSemanticVersions(
                        fw.firmwareVersion ?? 0,
                        isOcpp ? getFirmwareVersion(maxInstalled.ocppConfigKeys) : maxInstalled.installedFirmwareVersion
                    )
                )

            return firmwares
                ? firmwares.map((fw) => {
                      return { value: fw.id, label: fw.firmwareVersion }
                  })
                : []
        } else {
            return []
        }
    }, [firmwaresResponse])

    useEffect(() => {
        if (chargePoints.length != chargePoints.filter((x) => x.isOcpp === chargePoints[0].isOcpp).length) {
            setIsDifferentFirmwareTypes(true)
            return
        }
    }, [])

    useEffect(() => {
        if (firmwaresOptions.length > 0) {
            setSelectedVersion(firmwaresOptions[0].value)
        }
    }, [firmwaresOptions])

    const submit = () => {
        setIsSubmitted(true)
        onSubmit(selectedVersion)
            .catch(({ error }) => setError(error))
            .finally(() => setIsFinished(true))
    }

    useKeyboardHandle({ onEnterPress: submit, onEscapePress: () => onCancel() })

    const isEmptyState = firmwaresOptions.length === 0

    return (
        <BootstrapModalLayout
            size="lg"
            title={`Update chargers with firmware ${productTitle} ${firmwareTitle}`}
            actions={
                isFinished
                    ? []
                    : [
                          {
                              label: text('btnCancel'),
                              btn: 'btn-secondary',
                              callback: () => onCancel(),
                          },
                          {
                              label: text('btnUpdate'),
                              callback: submit,
                              disabled: isDifferentFirmwareTypes || !selectedVersion,
                          },
                      ]
            }
        >
            {isDifferentFirmwareTypes && (
                <SErrorAlert data-testid="alert-message">
                    You are not able to update OCPP and non-OCPP charger(s) at the same time. Select OCPP only or
                    non-OCPP only chargers for update.
                </SErrorAlert>
            )}
            <SFormContainer>
                {!isSubmitted && (
                    <SUpdateFormContainer>
                        {isEmptyState ? (
                            <SCentered className="margin-bottom-med aweFontSizeSmall">
                                You have the latest version of firmware
                            </SCentered>
                        ) : (
                            <>
                                <SSearchResultsNumber data-testid="no-of-chargers">
                                    {chargePoints.length} charger(s) selected for firmware update
                                </SSearchResultsNumber>
                                <div className="form-group">
                                    <SFilterLabel data-testid="select-fw">Select firmware version</SFilterLabel>
                                    <Select
                                        isDisabled={isDifferentFirmwareTypes}
                                        isSearchable={true}
                                        filterOption={(option, inputValue) => {
                                            const { label } = option
                                            return label.toString().includes(inputValue)
                                        }}
                                        options={firmwaresOptions}
                                        defaultValue={firmwaresOptions?.[0]}
                                        value={firmwaresOptions.find((option) => option.value === selectedVersion)}
                                        onChange={(option) => setSelectedVersion(option?.value)}
                                        styles={{ control: (base) => ({ ...base, fontSize: 14 }) }}
                                    />
                                </div>
                            </>
                        )}
                    </SUpdateFormContainer>
                )}

                {isSubmitted && !isFinished && (
                    <SCentered>
                        <Spinner />
                        <SSpinnerTitle>Updating...</SSpinnerTitle>
                    </SCentered>
                )}

                {isFinished && (
                    <SCentered>
                        {error ? (
                            <SFinishModalContainer>
                                <SIconContainer data-testid="icon-error">
                                    <IconError />
                                </SIconContainer>
                                <p data-testid="message-error">
                                    Update firmware to charger failed. Please try again later.
                                </p>
                                error code: {error}
                            </SFinishModalContainer>
                        ) : (
                            <SFinishModalContainer>
                                <SIconContainer data-testid="icon-success">
                                    <IconSuccess />
                                </SIconContainer>
                                <p data-testid="message-success">
                                    {chargePoints.length} charger(s) has been scheduled for update with selected
                                    firmware. Please check the progress for each charger in the Update Chargers page.
                                </p>
                            </SFinishModalContainer>
                        )}
                        <SButtonContainer>
                            <Button
                                primary={false}
                                onClick={() => onCancel(true)}
                                label={text('btnClose')}
                                data-testid="cancel-btn-1"
                            />
                        </SButtonContainer>
                    </SCentered>
                )}
            </SFormContainer>
        </BootstrapModalLayout>
    )
}

export default UpdateChargersFirmwareModal
